<template>
    <div v-if="!isLoading && orderDetails != null">
        <div class="md-layout">
            <div class="md-layout-item">
                <div class="custom-toolbar">
                    <div class="custom-toolbar-start">
                        <router-link
                            :to="{
                                name: 'Orders List'
                            }"
                        >
                            <h3 class="title">Order List</h3>
                        </router-link>
                        <h3 class="title">&nbsp;&nbsp; > &nbsp;&nbsp; {{ orderDetails.orderNumber }}</h3>
                    </div>
                    <div class="custom-toolbar-end" v-if="!$root.isMobileOnly">
                        <div class="status-box-container">
                            <div
                                class="order-status-box"
                                :class="`status-${$_getBadgeStatusColor(orderDetails.status)}`"
                            >
                                {{ orderDetails.status }}
                            </div>
                        </div>
                        <div class="header-button-container">
                            <SendSmsButton
                                v-if="hasBulkSmsAccess"
                                class-name="header-button"
                                :order-id="orderDetails.orderId"
                                :order-contact="orderDetails.contact"
                            />
                            <md-button
                                title="Update order"
                                class="md-warning"
                                @click="updateOrder(orderDetails.orderId)"
                                :disabled="isFetchingStops"
                            >
                                <span>Edit</span>
                            </md-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="order-details-grid">
            <div class="order-details-section">
                <md-card>
                    <md-card-header class="md-card-header-icon md-card-header-blue">
                        <div class="card-icon">
                            <md-icon>description</md-icon>
                        </div>
                    </md-card-header>

                    <md-card-content>
                        <div class="multiple-details-box">
                            <div class="details-box">
                                <label>Order Number</label>
                                <span>
                                    {{ orderDetails.orderNumber }}
                                </span>
                            </div>
                            <div class="details-box">
                                <label>Purchase Order Number</label>
                                <span>
                                    {{ orderDetails.purchaseOrderNumber }}
                                </span>
                            </div>
                            <div class="details-box">
                                <label>Source Reference</label>
                                <span>
                                    {{ orderDetails.sourceReference }}
                                </span>
                            </div>
                            <div class="details-box">
                                <label>Payment date</label>
                                <span>
                                    {{ orderDetails.paymentDate | dateFormat(DATE_TYPES.standardDate) }}
                                </span>
                            </div>
                        </div>

                        <div class="multiple-details-box">
                            <div class="details-box">
                                <label>Customer Name</label>
                                <span>
                                    {{ orderDetails.customer != null ? orderDetails.customer.name : '' }}
                                </span>
                            </div>
                            <div class="details-box">
                                <label>Team Region</label>
                                <span>
                                    {{ orderDetails.teamRegion != null ? orderDetails.teamRegion.name : '' }}
                                </span>
                            </div>
                            <div class="details-box">
                                <label>Service Area</label>
                                <span>
                                    {{ orderDetails.serviceArea }}
                                </span>
                            </div>
                            <div class="details-box">
                                <label>Service Package</label>
                                <span>
                                    {{ orderDetails.servicePackage }}
                                </span>
                            </div>
                            <div class="details-box">
                                <label>Handling Method</label>
                                <span>
                                    {{ orderDetails.handlingMethod }}
                                </span>
                            </div>
                        </div>

                        <div class="multiple-details-box">
                            <div class="details-box">
                                <label>Order date</label>
                                <span>
                                    {{ orderDetails.orderDate | dateFormat(DATE_TYPES.standardDate) }}
                                </span>
                            </div>
                            <div class="details-box">
                                <label>Expected Delivery Date</label>
                                <span>
                                    {{ orderDetails.expectedDeliveryDate | dateFormat(DATE_TYPES.standardDate) }}
                                </span>
                            </div>
                            <div class="details-box">
                                <label>Ready Available Pickup Date</label>
                                <span>
                                    {{ orderDetails.readyPickupAvailableDate | dateFormat(DATE_TYPES.standardDate) }}
                                </span>
                            </div>
                            <div class="details-box">
                                <label>Ready Available Delivery Date</label>
                                <span>
                                    {{ orderDetails.readyDeliveryAvailableDate | dateFormat(DATE_TYPES.standardDate) }}
                                </span>
                            </div>
                        </div>

                        <div class="address-details" v-if="orderDetails.pickupAddress != null">
                            <div class="details-box">
                                <label>Pickup Address</label>
                                <div v-if="orderDetails.pickupAddress.name" class="address-name">
                                    {{ orderDetails.pickupAddress.name }}
                                </div>
                                <span>{{ orderDetails.pickupAddress.address }}</span>
                            </div>
                        </div>
                        <div class="address-details" v-if="orderDetails.shippingAddress != null">
                            <div class="details-box">
                                <label>Delivery Address</label>
                                <div v-if="orderDetails.shippingAddress.name" class="address-name">
                                    {{ orderDetails.shippingAddress.name }}
                                </div>
                                <span>{{ orderDetails.shippingAddress.address }}</span>
                            </div>
                        </div>

                        <div
                            class="multiple-details-box"
                            v-if="
                                user.vehicleCapacityUnitsConfiguration != null &&
                                    user.vehicleCapacityUnitsConfiguration.length > 0
                            "
                        >
                            <div class="details-box">
                                <label>Quantity</label>
                                <span>
                                    {{ orderDetails.quantity }}
                                </span>
                            </div>
                            <div class="details-box">
                                <label>Volume</label>
                                <span v-if="orderDetails.volume">{{ orderDetails.volume }} {{ volumeSymbol }}</span>
                            </div>
                            <div class="details-box">
                                <label>Weight</label>
                                <span v-if="orderDetails.weight">{{ orderDetails.weight }} {{ weightSymbol }}</span>
                            </div>
                        </div>

                        <div
                            class="details-box md-chips md-primary md-theme-default md-has-placeholder md-theme-default"
                            v-if="orderDetails.skills.length > 0"
                        >
                            <label>Required skills:</label>
                            <div
                                class="md-chip md-theme-default custom-chip"
                                v-for="(skill, index) in orderDetails.skills"
                                :key="index"
                            >
                                {{ skill }}
                            </div>
                        </div>
                        <div class="details-box">
                            <label>Delivery Instructions</label>
                            <div>
                                {{ orderDetails.deliveryInstructions }}
                            </div>
                        </div>
                        <div class="details-box">
                            <label>Notes</label>
                            <div>
                                {{ orderDetails.notes }}
                            </div>
                        </div>
                        <div class="details-box">
                            <label>Schedule Link</label>
                            <div>
                                <a v-if="orderDetails.scheduleLink" :href="orderDetails.scheduleLink" target="_blank">
                                    <md-icon>place</md-icon>
                                    {{ orderDetails.scheduleLink }}
                                </a>
                                <a
                                    v-else-if="orderDetails.scheduleLink == null && !isReadOnlyUser"
                                    @click="createSchedulingLink(orderDetails.orderId)"
                                >
                                    <md-icon>add_location_alt</md-icon>
                                    Create scheduling link
                                </a>
                            </div>
                        </div>
                    </md-card-content>
                </md-card>
            </div>

            <div class="contact-details-section">
                <contact-details :stop-details="orderDetails" />
            </div>
            <div class="stops-table-section">
                <md-card>
                    <md-card-header class="md-card-header-icon md-card-header-blue">
                        <div class="card-icon">
                            <md-icon>drive_eta</md-icon>
                        </div>
                        <div class="card-header-actions">
                            <md-button
                                class="md-just-icon md-round md-white"
                                title="Refresh stops"
                                @click="refreshStops"
                                :disabled="isRefreshing"
                            >
                                <md-icon :class="{ rotating: isRefreshing }">refresh</md-icon>
                            </md-button>
                        </div>
                    </md-card-header>

                    <md-card-content>
                        <StopsTable
                            :order-stops="orderStops"
                            :is-loading="fetchStopStatusStateMachine === 'pending'"
                            :team-members="teamMembers"
                            :table-column-options="tableColumnOptions"
                            :column-keys="columnKeys"
                            :table-column-key-options="tableColumnKeyOptions"
                            :can-clone-jobs="canCloneJobs"
                            @statusHistoryUpdated="onChangedStopStatus"
                            @assignedUser="onAssignedUser"
                            @columnSwitch="$_handleColumnSwitch"
                            @trackingLinkClick="openTrackingLink"
                        />
                    </md-card-content>
                </md-card>
            </div>
        </div>
        <div class="main-container">
            <div class="left-container">
                <div class="order-lines-section">
                    <md-card>
                        <md-card-header class="md-card-header-icon md-card-header-rose">
                            <div class="card-icon">
                                <md-icon>receipt_long</md-icon>
                            </div>
                        </md-card-header>

                        <md-card-content>
                            <md-table v-if="orderDetails.orderLines.length > 0">
                                <md-table-row>
                                    <md-table-head>Product Reference</md-table-head>
                                    <md-table-head>Qty</md-table-head>
                                    <md-table-head>Status</md-table-head>
                                </md-table-row>
                                <md-table-row v-for="orderLine in orderDetails.orderLines" :key="orderLine.orderLineId">
                                    <md-table-cell>
                                        {{ orderLine.productReference }}
                                        <span v-if="orderLine.productVariant != null">
                                            ({{ orderLine.productVariant.size }} {{ orderLine.productVariant.colour }})
                                        </span>
                                    </md-table-cell>
                                    <md-table-cell>{{ orderLine.quantity }}</md-table-cell>
                                    <md-table-cell>
                                        <span class="custom-badge">
                                            {{ orderLine.status }}
                                        </span>
                                    </md-table-cell>
                                </md-table-row>
                            </md-table>
                            <div v-else>
                                <p class="no-result-message">No existing order lines for this order.</p>
                            </div>
                        </md-card-content>
                    </md-card>
                </div>

                <div class="item-lines-section">
                    <md-card>
                        <md-card-header class="md-card-header-icon md-card-header-green">
                            <div class="card-icon">
                                <md-icon>inventory_2</md-icon>
                            </div>
                        </md-card-header>

                        <md-card-content>
                            <md-table v-if="orderDetails.items.length > 0" class="itemline-table">
                                <md-table-row>
                                    <md-table-head>Status</md-table-head>
                                    <md-table-head>Barcode</md-table-head>
                                    <md-table-head>Volume</md-table-head>
                                    <md-table-head>Weight</md-table-head>
                                    <md-table-head>Description</md-table-head>
                                    <md-table-head>SKU</md-table-head>
                                    <md-table-head></md-table-head>
                                </md-table-row>

                                <md-table-row
                                    v-for="(item, index) in orderDetails.items"
                                    :key="item.itemId"
                                    class="expandable-row top-align"
                                >
                                    <md-table-cell>
                                        <span
                                            class="custom-badge"
                                            :class="`inventory-status-${item.status.toLowerCase().replace(/\s+/g, '')}`"
                                        >
                                            {{ $t('inputs.itemStatuses.' + item.status) }}
                                        </span>
                                    </md-table-cell>
                                    <md-table-cell>
                                        <router-link
                                            title="View item details"
                                            class="ref-link"
                                            :to="{
                                                name: 'Inventory Details',
                                                params: { itemId: item.itemId }
                                            }"
                                            target="_blank"
                                        >
                                            {{ item.barcode }}
                                        </router-link>
                                    </md-table-cell>
                                    <md-table-cell>{{ item.volume }} {{ volumeSymbol }}</md-table-cell>
                                    <md-table-cell>{{ item.weight }} {{ weightSymbol }}</md-table-cell>
                                    <md-table-cell>
                                        {{ item.description }}
                                        <div v-if="expandedRows[index]" class="expanded-content">
                                            <md-table
                                                class="mini-table"
                                                v-if="
                                                    item.itemLines.length > 0 &&
                                                        item.itemLines.filter((x) => x.quantity > 1).length >= 2
                                                "
                                            >
                                                <md-table-head>Qty</md-table-head>
                                                <md-table-head>Product</md-table-head>
                                                <md-table-row v-for="line in item.itemLines" :key="line.itemLineId">
                                                    <md-table-cell>
                                                        {{ line.quantity }}
                                                    </md-table-cell>

                                                    <md-table-cell>
                                                        {{ line.productReference }}
                                                    </md-table-cell>
                                                </md-table-row>
                                            </md-table>
                                        </div>
                                    </md-table-cell>
                                    <md-table-cell>{{ item.sku }}</md-table-cell>
                                    <md-table-cell>
                                        <md-button
                                            v-if="
                                                item.itemLines.length > 0 &&
                                                    item.itemLines.filter((x) => x.quantity > 1).length >= 2
                                            "
                                            class="md-icon-button md-primary"
                                            @click="toggleExpand(index)"
                                        >
                                            <md-icon>
                                                {{ expandedRows[index] ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
                                            </md-icon>
                                        </md-button>
                                    </md-table-cell>
                                </md-table-row>
                            </md-table>
                            <div v-else>
                                <p class="no-result-message">No existing items for this order.</p>
                            </div>
                        </md-card-content>
                    </md-card>
                </div>

                <div class="charges-section" v-if="!isIndividualUser">
                    <charge-rates-list
                        v-if="
                            ((orderDetails.chargeLines && orderDetails.chargeLines.length > 0) ||
                                orderDetails.customerId) &&
                                (canAccessRates || isAdmin || isReadOnlyUser)
                        "
                        :charge-lines="orderDetails.chargeLines || []"
                        :order-details="orderDetails"
                        title="Charges"
                        :has-invoice="orderDetails.customerInvoiceId !== null"
                        @updateOrderDetails="fetchOrderDetails"
                    />
                </div>

                <div class="notes-section">
                    <notes
                        :list="orderDetails.orderNotes"
                        :order-id="orderDetails.orderId"
                        @notesAdded="handleAddedOrderNote"
                    />
                </div>
            </div>

            <div class="right-container">
                <div class="md-layout-item" v-if="$root.isDesktop">
                    <md-card>
                        <md-card-header class="md-card-header-icon md-card-header-warning">
                            <div class="card-icon">
                                <md-icon>history</md-icon>
                            </div>
                        </md-card-header>

                        <md-card-content>
                            <time-line plain type="simple" v-if="statusHistory.length > 0">
                                <time-line-item
                                    inverted
                                    :badge-type="`status-${$_getBadgeStatusColor(history.newStatus)}`"
                                    badge-icon="place"
                                    v-for="(history, index) in statusHistory"
                                    :key="index"
                                    :is-clickable="false"
                                >
                                    <span
                                        slot="header"
                                        class="custom-badge"
                                        :class="`status-${$_getBadgeStatusColor(history.newStatus)}`"
                                    >
                                        {{ history.newStatus }}
                                    </span>

                                    <span slot="headerTime" class="time">
                                        {{ history.actionDate | dateTimeFormat(DATE_TYPES.standardDate) }}
                                    </span>

                                    <h6 slot="footer">
                                        <i class="ti-time" />

                                        By {{ history.changedByUserFullName }}
                                    </h6>
                                </time-line-item>
                            </time-line>
                            <p v-else class="grayedout-text">No Status History Available</p>
                        </md-card-content>
                    </md-card>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ContactDetails } from '@/pages/Stops/components';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { OrderMixin } from '@/mixins/OrderMixin';
import { handleRequests, showErrorMessage } from '@/helpers';
import { Notes, ChargeRatesList, TimeLine, TimeLineItem } from '@/components';
import { STATE_MACHINE_STATUS, DATE_TYPES, REFERENCE_SOURCE_KEY_OPTIONS } from '@/utils/constants';
import { STOP_TABLE_HEADER_DISPLAY_OPTION } from '@/utils/defaults';
import { mapGetters } from 'vuex';
import StopsTable from './components/StopsTable';
import SendSmsButton from './Buttons/SendSmsButton';

export default {
    name: 'OrderDetails',
    components: {
        ContactDetails,
        Notes,
        ChargeRatesList,
        StopsTable,
        SendSmsButton,
        TimeLine,
        TimeLineItem
    },
    mixins: [GeneralMixin, OrderMixin],
    data() {
        return {
            /**
             * @type {import('@/jsDocTypes/OrderViewModel').OrderViewModel}
             */
            orderDetails: null,
            isLoading: null,
            isRefreshing: false,
            expandedRows: [],
            fetchStopStatusStateMachine: STATE_MACHINE_STATUS.Idle,
            teamMembers: [],
            DATE_TYPES,
            orderStops: [],
            ...STOP_TABLE_HEADER_DISPLAY_OPTION(),
            statusHistory: [],
            volumeSymbol: null,
            weightSymbol: null
        };
    },
    computed: {
        isTeamOwner() {
            return this.$store.getters['user/isTeamOwner'];
        },
        isAdmin() {
            return this.$store.getters['user/isAdmin'];
        },
        isReadOnlyUser() {
            return this.$store.getters['user/isReadOnlyUser'];
        },
        isIndividualUser() {
            return this.$store.getters['user/isIndividualUser'];
        },
        canCloneJobs() {
            return this.$store.getters['user/canCloneJobs'];
        },
        hasBulkSmsAccess() {
            return this.$store.getters['user/hasBulkSmsAccess'];
        },
        isFetchingStops() {
            return this.fetchStopStatusStateMachine === STATE_MACHINE_STATUS.Pending;
        },
        ...mapGetters({
            user: 'user/user',
            canAccessRates: 'user/canAccessRates'
        })
    },
    async created() {
        this.$_applyColumnSwitchDefaults('orders.defaults.stop-table-options', {
            [this.columnKeys.referenceSource]: REFERENCE_SOURCE_KEY_OPTIONS
        });
    },
    async mounted() {
        const promises = [this.fetchOrderDetails(), this.fetchStops(), this.getTeamMembers(), this.getStatusHistory()];
        const result = await Promise.allSettled(promises);
        result.forEach((item) => {
            if (item.status === 'rejected') {
                const message = 'Error in loading the details';
                showErrorMessage(this, message, item.reason);
            }
        });

        const { symbol } = this.user.vehicleCapacityUnitsConfiguration.find((x) => x.type === 'volume');
        this.volumeSymbol = symbol;
        const { symbol: weightSymbol } = this.user.vehicleCapacityUnitsConfiguration.find((x) => x.type === 'weight');
        this.weightSymbol = weightSymbol;
    },
    methods: {
        async getTeamMembers(tripDate = null) {
            this.teamMembers = await this.$store.dispatch('team/FETCH_TEAM_MEMBERS', {
                date: tripDate
            });
        },
        async fetchStops() {
            this.fetchStopStatusStateMachine = STATE_MACHINE_STATUS.Pending;
            try {
                const api = `/api/orders/${this.$route.params.orderId}/stops`;
                const response = await handleRequests(api);
                const {
                    data: { responses }
                } = response;
                this.orderStops = responses;
                this.fetchStopStatusStateMachine = STATE_MACHINE_STATUS.Idle;
            } catch (e) {
                this.fetchStopStatusStateMachine = STATE_MACHINE_STATUS.Error;
            }
        },
        async fetchOrderDetails() {
            try {
                this.isLoading = true;
                this.$_handleLoaderState(true);
                const api = `/api/orders/${this.$route.params.orderId}`;

                const response = await handleRequests(api);

                this.orderDetails = response.data;

                this.isLoading = false;
                this.$_handleLoaderState(false);
            } catch (error) {
                this.$_handleLoaderState(false);
                const message = 'Error in loading the details';
                showErrorMessage(this, message, error);
            }
        },
        updateOrder(orderId) {
            this.$router.push({ name: 'Edit Order', params: { orderId } });
        },
        async handleAddedOrderNote() {
            await this.refreshNoteList();
        },
        async refreshNoteList() {
            const api = `/api/orders/${this.$route.params.orderId}/notes`;
            const result = await handleRequests(api);
            this.orderDetails.orderNotes = result.data;
        },
        toggleExpand(index) {
            // Toggle the expanded state of the clicked row
            this.$set(this.expandedRows, index, !this.expandedRows[index]);
        },
        async createSchedulingLink(orderId) {
            const api = `/api/orders/${orderId}/expirable-link`;
            const payload = {
                method: 'post'
            };

            try {
                const response = await handleRequests(api, payload);
                this.orderDetails.scheduleLink = response.data.url;
                this.$notify({
                    message: 'Successfully created a scheduling link.',
                    type: 'success'
                });
            } catch (e) {
                const message = 'Error in creating the scheduling link.';
                showErrorMessage(this, message, e);
            }
        },
        async onChangedStopStatus(response) {
            // Handle stop status change
            await this.refreshStops();
        },
        async onAssignedUser(response) {
            // Handle user assignment
            await this.refreshStops();
        },
        openTrackingLink(trackingLink) {
            window.open(trackingLink, '_blank');
        },
        async refreshStops() {
            try {
                this.isRefreshing = true;
                await this.fetchStops();
                this.$notify({
                    message: 'Stops refreshed successfully.',
                    type: 'success'
                });
            } catch (error) {
                const message = 'Error refreshing stops';
                showErrorMessage(this, message, error);
            } finally {
                this.isRefreshing = false;
            }
        },
        async getStatusHistory() {
            const api = `/api/orders/${this.$route.params.orderId}/status-history`;
            const { data } = await handleRequests(api);
            this.statusHistory = data.data || [];
        }
    }
};
</script>

<style lang="scss" scoped>
.md-card {
    margin: 0;
    z-index: auto;
}

/* Status Box Styles */
.status-box-container {
    display: flex;
    align-items: center;
    margin-right: 15px;
}

.order-status-box {
    padding: 6px 16px;
    color: #fff;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
/* CSS Grid Layout */
.order-details-grid {
    margin-top: 30px;
    padding: 0 15px;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-areas:
        'orderDetails orderDetails contactDetails'
        'stopsTable stopsTable stopsTable';
    column-gap: 20px;
    row-gap: 40px;
}

::v-deep .dropdown-menu {
    z-index: 1000;
}

.order-details-section {
    grid-area: orderDetails;
}

.contact-details-section {
    grid-area: contactDetails;
}

.stops-table-section {
    grid-area: stopsTable;
}

.order-lines-section {
    grid-area: orderLines;
}

.item-lines-section {
    grid-area: itemLines;
}

.charges-section {
    grid-area: charges;
}

::v-deep .md-field.md-theme-default.md-has-value::after {
    bottom: 0px !important;
    display: none !important;
}

::v-deep .md-field.md-theme-default.md-has-value::before {
    bottom: 0px !important;
    display: none !important;
}

.notes-section {
    grid-area: notes;
}

/* Responsive adjustments */
@media (max-width: 1200px) {
    .order-details-grid {
        grid-template-columns: 1fr;
        grid-template-areas:
            'orderDetails'
            'contactDetails'
            'stopsTable';
    }
}

@media (min-width: 1201px) and (max-width: 1600px) {
    .order-details-grid {
        grid-template-columns: 1fr 1fr 1fr;
        grid-template-areas:
            'orderDetails orderDetails contactDetails'
            'stopsTable stopsTable stopsTable';
    }
}

.multiple-details-box {
    > div {
        display: inline-block;
        width: 200px;
        vertical-align: top;
    }
}

.itemline-table {
    .top-align {
        vertical-align: top;
    }
}

.details-box {
    a:hover {
        cursor: pointer;
    }
}

.mini-table {
    ::v-deep .md-table-head {
        padding: 5px;
        line-height: 10px;
    }
    ::v-deep .md-table-head-label {
        font-size: 0.8rem;
        font-weight: 500;
    }

    .md-table-row .md-table-cell {
        padding: 8px 8px;
        height: 30px;
    }

    ::v-deep table {
        border: 1px solid #ddd;
        margin-top: 10px;
    }
}

.mini-table-row {
    padding: 0;
}

.expandable-row {
    transition: all 0.3s ease;
}

/* Card header actions */
.card-header-actions {
    position: absolute;
    top: 15px;
    right: 15px;
}

.card-header-actions .md-button {
    min-width: 40px;
    width: 40px;
    height: 40px;
    margin: 0;
}

/* Rotating animation for refresh button */
@keyframes rotate {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

.rotating {
    animation: rotate 1s linear infinite;
}

.main-container {
    display: flex;
    align-items: flex-start;
    margin-top: 30px;
    padding: 0 15px;
}

.left-container {
    flex: 0 0 70%;
}

.left-container > * {
    margin-top: 20px;
    margin-bottom: 40px;
}

.right-container {
    flex: 0 0 30%;
}

.right-container > * {
    margin-top: 20px;
    margin-bottom: 40px;
}

::v-deep .timeline.timeline-simple {
    margin-top: 0;
}

::v-deep .md-card-timeline {
    margin-top: 0;
    margin-bottom: 0;
}

::v-deep .timeline > li > .timeline-panel {
    padding: 5px 20px;
    background-color: #fafafa;
}

::v-deep .timeline-panel > h6 > h6 {
    text-transform: none;
    margin-top: 0;
}

::v-deep .timeline-panel > h6 {
    margin-top: 0px;
}

::v-deep .timeline-heading {
    margin-bottom: 5px;

    > span {
        vertical-align: middle;
    }
}

::v-deep .timeline > li {
    margin-bottom: 5px;
}

::v-deep .timeline > li > .timeline-panel:after,
::v-deep .timeline > li > .timeline-panel:before {
    border-left-color: #fafafa;
    border-right-color: #fafafa;
}

::v-deep .timeline.timeline-simple > li > .timeline-panel {
    width: 83%;
}

::v-deep .timeline-heading .time {
    margin-left: 10px;
    color: #333333;
    font-weight: 400;
    font-size: 0.75rem;
}
</style>
